/**
 * Browser Extension Error Handler
 * Utility to handle and suppress browser extension connection errors
 * that don't affect the core application functionality
 */

class BrowserExtensionErrorHandler {
  constructor() {
    this.originalConsoleError = console.error;
    this.originalConsoleWarn = console.warn;
    this.suppressedErrors = [];
    this.extensionErrorPatterns = [
      /Could not establish connection.*Receiving end does not exist/i,
      /Extension context invalidated/i,
      /chrome-extension:\/\//i,
      /moz-extension:\/\//i,
      /safari-extension:\/\//i,
      /Error in event handler.*extension/i,
      /Unchecked runtime\.lastError/i,
      /port closed before a response was received/i,
      /The message port closed before a response was received/i
    ];
    
    this.isEnabled = false;
  }

  /**
   * Enable extension error suppression
   * Call this during app initialization
   */
  enable() {
    if (this.isEnabled) return;
    
    this.isEnabled = true;
    
    // Override console.error to filter extension errors
    console.error = (...args) => {
      const message = args.join(' ');
      
      if (this.isExtensionError(message)) {
        this.suppressedErrors.push({
          message,
          timestamp: new Date().toISOString(),
          args
        });
        
        // Optionally log a suppressed message in development
        if (process.env.NODE_ENV === 'development') {
          this.originalConsoleError(
            '%c🔕 Suppressed browser extension error:',
            'color: #fbbf24; font-weight: bold;',
            message
          );
        }
        return;
      }
      
      // Pass through non-extension errors
      this.originalConsoleError.apply(console, args);
    };
    
    // Override console.warn for extension warnings
    console.warn = (...args) => {
      const message = args.join(' ');
      
      if (this.isExtensionError(message)) {
        // Suppress extension warnings silently
        return;
      }
      
      this.originalConsoleWarn.apply(console, args);
    };
    
    console.log('🛡️ Browser extension error suppression enabled');
  }

  /**
   * Disable extension error suppression
   */
  disable() {
    if (!this.isEnabled) return;
    
    console.error = this.originalConsoleError;
    console.warn = this.originalConsoleWarn;
    this.isEnabled = false;
    
    console.log('🔓 Browser extension error suppression disabled');
  }

  /**
   * Check if a message is an extension-related error
   */
  isExtensionError(message) {
    return this.extensionErrorPatterns.some(pattern => pattern.test(message));
  }

  /**
   * Get statistics about suppressed errors
   */
  getStats() {
    const errorCounts = {};
    
    this.suppressedErrors.forEach(error => {
      const key = this.categorizeError(error.message);
      errorCounts[key] = (errorCounts[key] || 0) + 1;
    });
    
    return {
      totalSuppressed: this.suppressedErrors.length,
      categories: errorCounts,
      lastSuppressed: this.suppressedErrors[this.suppressedErrors.length - 1]?.timestamp
    };
  }

  /**
   * Categorize extension errors for statistics
   */
  categorizeError(message) {
    if (message.includes('Could not establish connection')) return 'Connection Errors';
    if (message.includes('Extension context invalidated')) return 'Context Errors';
    if (message.includes('chrome-extension://')) return 'Chrome Extension';
    if (message.includes('moz-extension://')) return 'Firefox Extension';
    if (message.includes('runtime.lastError')) return 'Runtime Errors';
    return 'Other Extension Errors';
  }

  /**
   * Clear suppressed error history
   */
  clearHistory() {
    this.suppressedErrors = [];
  }

  /**
   * Show a diagnostic report
   */
  showDiagnostic() {
    const stats = this.getStats();
    
    if (stats.totalSuppressed === 0) {
      console.log('✅ No browser extension errors detected');
      return;
    }
    
    console.group('🔍 Browser Extension Error Diagnostic');
    console.log(`Total suppressed errors: ${stats.totalSuppressed}`);
    console.log('Error categories:', stats.categories);
    console.log(`Last error: ${stats.lastSuppressed || 'None'}`);
    
    if (stats.totalSuppressed > 10) {
      console.warn('⚠️ High number of extension errors detected. Consider:');
      console.warn('   • Testing in incognito mode');
      console.warn('   • Disabling browser extensions');
      console.warn('   • Checking extension compatibility');
    }
    
    console.groupEnd();
  }
}

// Create global instance
const extensionErrorHandler = new BrowserExtensionErrorHandler();

// Auto-enable in production, optional in development
if (process.env.NODE_ENV === 'production') {
  extensionErrorHandler.enable();
}

// Make available globally for debugging
window.extensionErrorHandler = extensionErrorHandler;

export default extensionErrorHandler;
